
/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, defineComponent, ref, watchEffect, watch } from 'vue';
import MenuEditUser from '../menuEditUser/index.vue';
import { getInitials, numberFormattedWithCommas } from '../../utils/functions';
import FilesSection from '../filesSection/index.vue';
import Chips from '../chips/index.vue';
import DropDownStage from '../dropdownChangeStage/index.vue';
import { JobsAPI } from '../../services/jobs/index';
import TimeLineSection from '../timeLineSection/index.vue';
import { createToastAxelerate } from '../../utils/toast';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import MenuShare from '../menuShare/index.vue';
import { useState } from '../../composables/useState';
import EvaluationsAnswerFrom from '../evaluationAnswersForm/index.vue';
import Avatar from '../avatar/index.vue';
import { validStages, validStagesChallenge } from '../../constants/candidates';
import SwitchButton from '../switchButton/index.vue';
import ChallengeTab from '../challengeTab/index.vue';
import Loader from '../loader/index.vue';
import ModalRejectCandidate from '@/components/modalRejectCandidate/index.vue';
import BaseModal from '@/components/baseModal/index.vue';
import { usePromisedModal } from '../../composables/usePromisedModal';

const CandidateInfo = defineComponent({
  name: 'CandidateInfo',
  emits: [
    'openEdit',
    'deleteCandidate',
    'reloadCandidates',
    'reloadOne',
    'goToPublic',
    'resetSelected',
    'setLoadingStage',
    'rejectCandidate',
  ],
  components: {
    MenuEditUser,
    FilesSection,
    Chips,
    TimeLineSection,
    DropDownStage,
    MenuShare,
    EvaluationsAnswerFrom,
    Avatar,
    SwitchButton,
    ChallengeTab,
    ModalRejectCandidate,
    BaseModal,
  },
  props: {
    userInfo: {
      type: Object,
      required: true,
    },
    candidateId: {
      type: String,
      required: true,
    },
  },
  setup(props, context) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const path = route.path;
    const isLoading = ref(false);
    const confirmationModal = usePromisedModal<boolean>();
    const showConfirmationModal = ref(false);
    const [showRejected, setShowRejected] = useState(false);
    const [visible, setVisible] = useState(
      Boolean(props?.userInfo?.is_visible) || false
    );

    const jobId = route.params.id;
    const currentStage = computed(
      () => store.state.candidateInfo?.data?.current_stage
    );
    const stages: any = computed(() => props.userInfo.job_candidate_stages);
    const stageId = stages.value.find(
      (stages: any) => stages.job_stage.stage.name === 'Rejected'
    ).id;
    const changeCurrentStage = (id: string) => {
      isLoading.value = true;
      JobsAPI.updateCandidateStage(id)
        .then(async () => {
          await context.emit('resetSelected');
          store.dispatch('getJobState', jobId);
          createToastAxelerate(
            'Stage Candidate',
            'The candidate has been moved to stage successfully. ',
            'success'
          );

          await context.emit('reloadCandidates');
          isLoading.value = false;
        })
        .catch((err) => {
          isLoading.value = false;

          const {
            response: {
              data: { errors },
            },
          } = err;
          createToastAxelerate(
            'Change Stage',
            errors && errors[0] ? errors[0] : 'An error has ocurred',
            'danger'
          );
        });
    };
    const openEdit = async () => {
      await context.emit('openEdit');
    };
    const deleteCandidate = async () => {
      await context.emit('deleteCandidate');
    };
    const goToPublic = async () => {
      await context.emit('goToPublic');
    };
    const reloadCandidates = async () => {
      store.dispatch('getJobState', jobId);
      await context.emit('resetSelected');
      await context.emit('reloadCandidates');
    };
    const buildOverYears = (year: any, isCurrent: any) => {
      if (isCurrent) {
        return `(Now)`;
      }
      const formatYear = new Date().getFullYear();
      return `(Over ${formatYear - year} Years)`;
    };
    const buildNow = (year: any, isCurrent: any) => {
      if (isCurrent) {
        return `Now`;
      }
      return `${year}`;
    };
    const tabs = ref(['Timeline', 'Profile', 'Files']);
    const activeTab = ref<any>('Timeline');
    watchEffect(() => {
      if (props?.userInfo?.id) {
        activeTab.value = route.params.tab;
      }

      if (validStages.includes(currentStage.value)) {
        if (validStagesChallenge.includes(currentStage.value)) {
          tabs.value = [
            'Timeline',
            'Profile',
            'Files',
            'Talent evaluation',
            'Challenge',
          ];
        } else {
          tabs.value = ['Timeline', 'Profile', 'Files', 'Talent evaluation'];
        }
      }
    });

    const renderDropDown = (stage: string) => {
      switch (stage) {
        case 'Hiring':
          return false;
        case 'Rejected':
          return true;
        default:
          return true;
      }
    };
    const buildConvertions = (mxn: string, usd: string) => {
      return `Salary expectation: $${numberFormattedWithCommas(
        parseFloat(mxn)
      )} MXN (${numberFormattedWithCommas(parseFloat(usd))} USD)`;
    };
    const buildConvertionsCurrent = (mxn: string, usd: string) => {
      return `Current salary: $${numberFormattedWithCommas(
        parseFloat(mxn)
      )} MXN (${numberFormattedWithCommas(parseFloat(usd))} USD)`;
    };
    const validateLastStage = async (name: string, changeId: string) => {
      if (name === 'Hired') {
        showConfirmationModal.value = true;
        const confirmed = await confirmationModal.ask();
        if (confirmed) {
          showConfirmationModal.value = false;
          changeCurrentStage(changeId);
        } else {
          showConfirmationModal.value = false;
        }
      } else {
        changeCurrentStage(changeId);
      }
    };
    const reloadOne = () => {
      context.emit('reloadOne');
    };
    const updateVisibility = (e: any) => {
      setVisible(e);
      const payload = {
        job_candidate: {
          is_visible: e,
        },
      };
      const candidateId = props?.userInfo?.id;
      JobsAPI.updateJobCandidateVisibility(jobId, candidateId, payload).then(
        async () => {
          await createToastAxelerate(
            'Candidate Visibility',
            'The visibility has been changed',
            'success'
          );
          await reloadOne();
        }
      );
    };

    const changeCandidateTab = (index: number) => {
      activeTab.value = tabs.value[index];
      router.replace({ params: { tab: activeTab.value } });
    };

    const setLoadingStage = async () => {
      isLoading.value = !isLoading.value;
    };
    const rejectCandidate = async () => {
      setShowRejected(true);
    };

    watch(
      () => route.params.job_candidate_id,
      () => {
        if (!route.params.tab) {
          router.replace({ params: { tab: 'Timeline' } });
        }
      }
    );
    return {
      buildConvertions,
      buildConvertionsCurrent,
      openEdit,
      deleteCandidate,
      getInitials,
      activeTab,
      tabs,
      buildOverYears,
      reloadCandidates,
      buildNow,
      changeCurrentStage,
      numberFormattedWithCommas,
      goToPublic,
      showConfirmationModal,
      confirmationModal,
      validateLastStage,
      path,
      currentStage,
      visible,
      updateVisibility,
      reloadOne,
      changeCandidateTab,
      Loader,
      renderDropDown,
      isLoading,
      setLoadingStage,
      showRejected,
      setShowRejected,
      stageId,
      rejectCandidate,
    };
  },
});

export default CandidateInfo;
